import '@css/base/carousel.css'

import anime from 'animejs/lib/anime.es.js'

import { App } from '../core/App.js'
import { Raf } from '../core/Raf.js'
import { Config } from '../core/Config.js'
import { Device } from '../core/Device.js'
import { isElement } from '../utils/Is.js'
import { Id } from '../utils/Id.js'
import { damp } from '../utils/Math.js'
import { transformX, transformY } from '../utils/Transform.js'
import { inViewport } from '../utils/InViewport.js'
import { Events, EventType } from '../utils/Events.js'
import { isFirefox, isSafari } from '../utils/Support.js'
import { Sleep } from '../utils/Sleep.js'
import { Clock } from '../utils/Clock.js'
import { Layout } from '../core/Layout.js'

import normalizeWheel from 'normalize-wheel-es'

export class Scroll {
  constructor(args) {
    this.options = {
      events: true,
      page: document,
      selector: '[data-scroll]',
      selectorChild: '[data-scroll-item]',
      selectorGroup: '[data-scroll-items]',
      infinite: false,
      horizontal: false,
      snap: false,
      propagation: true,
      scrollY: true,
      debug: false,
      scrollX: true,
      sync: false,
      drag: Config.DRAG,
      mainScroll: false,
      autoScroll: false,
      autoScrollSpeed: 1,
      smooth: Config.SMOOTH,
    }

    Object.assign(this.options, args)

    if (!isElement(this.options.selector)) {
      this.el = this.options.page.querySelector(this.options.selector)
    } else {
      this.el = this.options.selector !== undefined ? this.options.selector : null
    }

    this.id = Id('scroll')

    this.init()
    this.addEvents()

    if (this.options.sync) Events.emit(EventType.SCROLL_SYNC, this)
    if (this.options.mainScroll) Layout.scroll = this

    this.resetScroll()
  }

  async init() {
    this.setDefaults()

    this.animate = this.animate.bind(this)
    this.onWheel = this.onWheel.bind(this)
    this.onResize = this.onResize.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.getSections = this.getSections.bind(this)
    this.getSection = this.getSection.bind(this)
    this.onFocus = this.onFocus.bind(this)

    this.scrollTo = this.scrollTo.bind(this)

    this.activate = this.activate.bind(this)
    this.start = this.start.bind(this)
    this.stop = this.stop.bind(this)
    this.mouseDownHandler = this.mouseDownHandler.bind(this)
    this.mouseMoveHandler = this.mouseMoveHandler.bind(this)
    this.mouseUpHandler = this.mouseUpHandler.bind(this)
    this.onSwipe = this.onSwipe.bind(this)

    this.selector = this.options.selector
    this.selectorChild = this.options.selectorChild
    this.selectorGroup = this.options.selectorGroup
    this.drag = this.options.drag

    this.container = this.el || document.querySelector(this.selector)

    this.els = [...this.el.querySelectorAll(`${this.selectorChild}`)]
    this.elGroups = [...this.el.querySelectorAll(`${this.selectorGroup}`)]

    this.links = [...this.el.querySelectorAll('a[href]')]

    if (!this.options.smooth) this.options.smooth = Config.SMOOTH
    if (!this.options.snap) this.options.snap = this.el.hasAttribute('data-scroll-snap')
    if (!this.options.infinite) this.options.infinite = this.el.hasAttribute('data-scroll-infinite')
    if (!this.options.horizontal) this.options.horizontal = this.el.hasAttribute('data-scroll-horizontal')
    if (!this.options.sync) this.options.sync = this.el.hasAttribute('data-scroll-sync')
    if (!this.options.scrollShim) this.options.scrollShim = this.el.hasAttribute('data-scroll-shim')

    this.y = 0
    this.x = 0
    this.delta = 0

    this.time = new Clock()

    this.isScrolling = false
    this.scrollClassAdded = false
    this.isDraggingScrollbar = false
    this.resizing = false
    this.scrollDirection = 'forwards'

    document.body.classList.remove('is-scrolling')

    if (this.drag) this.activatePointerEvents()

    this.offsetTop = 0

    await Sleep()

    this.onResize()

    await Sleep()

    if (!this.options.sync) {
      Raf.add(this.animate, this.id)
      Raf.moveToFirst(this.id)
    }
  }

  addEvents() {
    Events.on(EventType.RESIZE, this.onResize)

    if (!this.options.events) return

    const container = this.container

    container.addEventListener('wheel', this.onWheel, { passive: true })

    if (this.options.smooth) {
      addEventListener('keydown', this.onKeyDown, false)

      this.addScrollbarEvents()

      this.links.forEach((link) => {
        link.addEventListener('focus', this.onFocus)
      })

      if (this.drag) {
        if ('ontouchmove' in window) {
          container.addEventListener('touchstart', this.mouseDownHandler, {
            passive: true,
          })
          container.addEventListener('touchmove', this.mouseMoveHandler, {
            passive: true,
          })
          container.addEventListener('touchend', this.mouseUpHandler)
        } else {
          container.addEventListener('mousedown', this.mouseDownHandler)
          container.addEventListener('mousemove', this.mouseMoveHandler)
          container.addEventListener('mouseup', this.mouseUpHandler)
        }
      }
    }
  }

  onFocus(e) {
    // prevent scroll to top on focus
    e.preventDefault()
    e.target.blur() // remove focus
    // get bounds target
    const target = e.target
    // const targetBounds = target.getBoundingClientRect()
    // get bounds container

    // target.closest('[data-scroll-item]')
    // const containerBounds = this.container.getBoundingClientRect()
    // this.scrollTo(0)
  }

  setDefaults() {
    this.container = null
    this.els = []
    this.sections = []

    this.layout = {
      h: 0,
      w: 0,
    }

    this.settings = {
      interpolation: this.options.smooth ? 0.1 : 1,
      speed: 0.85,
      speedFirefox: 1.4,
      dragSpeed: 2,
      scrollToDuration: 1800,
    }

    this.pointer = {
      active: false,
      move: false,
      y: 0,
      x: 0,
    }

    this.index = {
      current: 1,
      total: 0,
    }

    this.progress = 0

    this.scroll = {
      current: 0,
      lerped: 0,
      limit: 0,
      delta: 0,
      deltaY: 0,
      dragY: 0,
      dragX: 0,
      max: 0,
    }

    this.state = {
      active: false,
      animating: false,
      ticking: false,
      gesture: false,
    }
  }

  activate() {
    this.onResize()
    this.state.active = true
    this.time.start()
    this.resetScroll()
  }

  resetScroll() {
    document.body.scrollTop = this.scroll.current = this.scroll.lerped = 0
    if (history.scrollRestoration) {
      history.scrollRestoration = 'manual'
    }
  }

  start() {
    this.state.ticking = true
  }

  stop() {
    this.state.ticking = false
  }

  async destroy() {
    Events.off(EventType.RESIZE, this.onResize)

    if (!this.options.events) return

    this.container.removeEventListener('wheel', this.onWheel, { passive: true })

    if (this.options.smooth) {
      window.removeEventListener('keydown', this.onKeyDown, false)

      this.links.forEach((link) => {
        link.removeEventListener('focus', this.onFocus)
      })

      if (this.drag) {
        if ('ontouchmove' in window) {
          this.container.removeEventListener('touchstart', this.mouseDownHandler, { passive: true })
          this.container.removeEventListener('touchmove', this.mouseMoveHandler, { passive: true })
          this.container.removeEventListener('touchend', this.mouseUpHandler)
        } else {
          this.container.removeEventListener('mousedown', this.mouseDownHandler)
          this.container.removeEventListener('mousemove', this.mouseMoveHandler)
          this.container.removeEventListener('mouseup', this.mouseUpHandler)
        }
      }
    }

    await Sleep()

    this.stop()

    this.container = null
    this.els = null
    this.elGroups = null

    await Sleep(200)

    if (!this.options.sync) Raf.remove(this.id)

    this.y = 0
    this.x = 0
  }

  async snapToClosest() {
    let closest
    this.sections.forEach((section, index) => {
      if (section.inView) {
        const current = -this.scroll.current
        const offsetHalf = section.elBounds.offsetTop - section.elBounds.h / 2
        if (current > offsetHalf) {
          this.index.current = index
          closest = section
        }
      }
    })

    await Sleep()
    Events.emit(`${closest.id}_PROGRESS`)
    this.scrollTo(-closest.elBounds.offsetTop)
  }

  scrollTo(y, duration = this.settings.scrollToDuration) {
    if (this.scrollAnimation) this.scrollAnimation.pause()
    let targetY = y
    if (this.options.horizontal) targetY += this.container.getBoundingClientRect().left
    if (targetY <= -this.scroll.max) targetY = -this.scroll.max
    this.scrollAnimation = anime({
      duration,
      targets: this.scroll,
      current: targetY,
      lerped: targetY,
      easing: 'easeOutExpo',
    })

    return this.scrollAnimation.finished
  }

  calculateScroll() {
    if (!this.options.smooth) {
      this.scroll.current = -document.body.scrollTop
      if (this.scroll.current < 0) {
        this.isScrolling = true
      } else {
        this.isScrolling = false
      }
    } else if (this.scroll.max < 0) {
      this.scroll.current = 0
      this.isScrolling = false
    } else {
      this.scroll.current = this.scroll.current + this.scroll.delta

      const isAtBottom = Math.abs(this.scroll.current) > this.scroll.max

      if (!this.options.infinite) {
        if (-this.scroll.current < 0) {
          this.scroll.current = 0
        } else if (isAtBottom) {
          this.scroll.current = -this.scroll.max
        }
      }

      this.isScrolling = -this.scroll.current > 20
    }

    if (this.isScrolling && !this.scrollClassAdded) {
      document.body.classList.add('is-scrolling')
      this.scrollClassAdded = true
    }

    if (!this.isScrolling && this.scrollClassAdded) {
      document.body.classList.remove('is-scrolling')
      this.scrollClassAdded = false
    }
  }

  lerpScroll() {
    let deltaTime = this.time.getDelta()

    this.scroll.current = this.options.autoScroll ? this.scroll.current - this.options.autoScrollSpeed : this.scroll.current

    this.scroll.lerped = this.options.smooth && !isSafari ? damp(this.scroll.lerped, this.scroll.current, 5, deltaTime) : damp(this.scroll.lerped, this.scroll.current, 10, deltaTime)

    if (this.options.horizontal) {
      this.x = this.scroll.lerped
    } else {
      this.y = this.scroll.lerped
    }
  }

  transformSection({ active, el, partsTop, parts, partsBottom, parent, inView, id, elBounds }, scroll) {
    el.inView = inView

    if (inView) {
      active = true

      if (this.options.smooth) {
        el.vscroll = scroll

        if (this.options.horizontal) {
          transformX(el, scroll)
        } else {
          transformY(el, scroll)
        }

        el.style.visibility = ''

        if (partsTop.length) {
          for (let i = 0; i < partsTop.length; i++) {
            this.transformPart(partsTop[i], scroll)
          }
        }

        // if (partsBottom.length) {
        //   for (let i = 0; i < partsBottom.length; i++) {
        //     const part = partsBottom[i]
        //     const transformDistance = (this.scroll.max + this.scroll.lerped) * (part.parallax * 0.1)
        //     part.el.parallax = transformDistance
        //     if (this.options.horizontal) {
        //       transformX(part.el, transformDistance)
        //     } else {
        //       transformY(part.el, transformDistance)
        //     }
        //   }
        // }
      }

      if (parts.length) {
        for (let i = 0; i < parts.length; i++) {
          const part = parts[i]

          if (this.options.smooth) {
            const scroll = -this.scroll.lerped
            const scrollMiddle = scroll + this.layout.h / 2
            const transformDistance = (scrollMiddle - part.middle) * -part.parallax

            part.el.parallax = transformDistance

            if (this.options.horizontal) {
              transformX(part.el, transformDistance)
              part.el.transform.x = transformDistance
            } else {
              transformY(part.el, transformDistance)
              part.el.transform.y = transformDistance
            }
          }
        }
      }
    } else {
      if (this.options.smooth) {
        el.style.visibility = 'hidden'
      }

      active = false
    }
  }

  checkInView(sections) {
    for (let i = 0; i < sections.length; i++) {
      sections[i].inView = inViewport(sections[i].elBounds, this.scroll.lerped - sections[i].scrollOffset, {
        parent: sections[i].parent,
        horizontal: this.options.horizontal,
        tolerance: 300,
        w: this.layout.w,
        h: this.layout.h,
      })
    }
  }

  transformPart({ el, parallax }, scroll) {
    el.parallax = scroll * parallax - scroll
    if (this.options.horizontal) {
      transformX(el, scroll * parallax - scroll)
    } else {
      transformY(el, scroll * parallax - scroll)
    }
  }

  transformSections(sections) {
    const { lerped } = this.scroll
    for (let i = 0; i < sections.length; i++) {
      this.updateImages(sections[i], lerped - sections[i].scrollOffset)
      this.transformSection(sections[i], lerped - sections[i].scrollOffset)
    }
  }

  updateImages({ images }, scroll) {
    if (!images.length) return
    for (let i = 0; i < images.length; i++) {
      if (this.options.horizontal) {
        images[i].updateX(-scroll)
      } else {
        images[i].updateY(-scroll)
      }
    }
  }

  animate(t) {
    if (this.resizing) return

    if (this.state.active) {
      if (!this.state.animating) this.calculateScroll()
    }

    this.checkInView(this.sections)
    this.calcOffsets()
    this.lerpScroll()
    if (!this.options.scrollShim) this.transformSections(this.sections)

    this.scroll.delta = 0
  }

  calcOffsets() {
    const { infinite, horizontal } = this.options
    const { w, h } = this.layout
    const { lerped, current, max } = this.scroll
    const { sections } = this

    if (!infinite) {
      for (let i = 0; i < sections.length; i++) {
        sections[i].elBounds.current = lerped
      }
      return
    }

    if (infinite) {
      const diff = lerped - current

      const layoutRef = horizontal ? w : h
      const offsetRef = horizontal ? 'offsetLeft' : 'offsetTop'

      if (-lerped > max) {
        this.scroll.lerped = layoutRef
        this.scroll.current = this.scroll.lerped - diff
      } else if (-lerped + layoutRef < 0) {
        this.scroll.lerped = -this.scroll.max
        this.scroll.current = this.scroll.lerped - diff
      }

      for (let i = 0; i < sections.length; i++) {
        if (Math.abs(sections[i].elBounds[offsetRef]) / 2 > Math.abs(this.scroll.lerped - layoutRef)) {
          sections[i].scrollOffset = max + layoutRef
        } else {
          sections[i].scrollOffset = 0
        }
        sections[i].elBounds.current = this.scroll.lerped - sections[i].scrollOffset
      }
    }
  }

  onSwipe(e) {
    if (!Device.mobile) return

    if (e.deltaY > 60) this.toPrevious()
    if (e.deltaY < -60) this.toNext()
  }

  mouseDownHandler(e) {
    if (this.dragComponent) return

    if (this.scrollAnimation) this.scrollAnimation.pause()

    if (!this.state.active || this.state.animating) return false

    if (this.pointer.active) return

    this.pointer.active = true
    this.state.ticking = true

    this.pointer.y = e.touches ? e.touches[0].clientY : e.clientY
    this.pointer.x = e.touches ? e.touches[0].clientX : e.clientX

    const scrollPos = this.scroll.current

    if (this.drag) {
      setTimeout(() => {
        const diff = this.scroll.current - scrollPos
        if(Math.abs(diff) > 1) {
          this.deactivatePointerEvents()
        }
      }, 150);
    }
  }

  mouseMoveHandler(e) {
    if (!this.options.propagation) e.stopPropagation()

    if (!this.state.active || this.state.animating) return false

    if (this.mouseMoveTimeOut1) clearTimeout(this.mouseMoveTimeOut1)
    if (this.mouseMoveTimeOut2) clearTimeout(this.mouseMoveTimeOut2)
    if (this.dragComponent) return
    if (!this.pointer.active) return

    if (this.drag) this.deactivatePointerEvents()

    if (this.options.snap) this.clearSnapTl()
    this.start()

    this.checkInView(this.sections)

    const y = e.touches ? e.touches[0].clientY : e.clientY
    const x = e.touches ? e.touches[0].clientX : e.clientX

    this.pointer.move = true

    this.scroll.dragX = this.options.horizontal ? x - this.pointer.x : 0
    this.scroll.dragY = this.options.horizontal ? 0 : y - this.pointer.y

    this.scroll.current += (this.scroll.dragX + this.scroll.dragY) * this.settings.dragSpeed

    this.pointer.y = y
    this.pointer.x = x

    Events.emit(EventType.MOUSE_DRAG)

    this.mouseMoveTimeOut1 = setTimeout(() => {
      this.pointer.active = false
    }, 100)

    this.mouseMoveTimeOut2 = setTimeout(() => {
      this.pointer.active = false
      this.pointer.move = false
      this.stop()
    }, 1000)
  }

  mouseUpHandler() {
    this.pointer.active = false
    if (this.drag) this.activatePointerEvents()

    if (this.options.snap) {
      this.snapTimeOut = setTimeout(() => {
        this.snapToClosest()
      }, 500)
    }

    setTimeout(() => {
      this.pointer.move = false
    }, 1000)

    this.scroll.dragY = 0
  }

  clearSnapTl() {
    if (this.snapTimeOut) {
      clearTimeout(this.snapTimeOut)
      this.scrollAnimation && this.scrollAnimation.pause()
    }
  }

  checkGestures(deltaY, margin = 50) {
    this.clearSnapTl()
    clearTimeout(this.gestureTl)
    this.state.gesture = true
    if (deltaY < -margin) {
      this.toNext()
    }

    if (deltaY > margin) {
      this.toPrevious()
    }

    this.gestureTl = setTimeout(() => {
      this.state.gesture = false
    }, 300)
  }

  onWheel(e) {
    if (this.options.smooth) {
      const { pixelX, pixelY } = normalizeWheel(e)

      const { scrollX, scrollY } = this.options

      const newDeltaX = scrollX ? Math.max(Math.min(-1 * pixelX, 300), -300) : 0
      const newDeltaY = scrollY ? Math.max(Math.min(-1 * pixelY, 300), -300) : 0

      const newDelta = this.options.horizontal ? (Math.abs(newDeltaX) >= Math.abs(newDeltaY) ? newDeltaX : newDeltaY) : newDeltaY

      if (!this.state.active || this.state.animating) return false

      if (this.wheelTimeOut) {
        clearTimeout(this.wheelTimeOut)
      }

      if (this.options.snap) this.clearSnapTl()
      this.start()

      // const horizontalDelta = this.options.horizontal ? newDeltaX : 0
      // let calcDelta
      // if(Math.abs(newDeltaY) > Math.abs(horizontalDelta)) {
      //   calcDelta = newDeltaY
      // } else {
      //   calcDelta = horizontalDelta
      // }

      this.scroll.delta = newDelta
      this.scroll.delta *= this.settings.speed

      this.delta = this.scroll.delta

      this.scrollDirection = this.scroll.delta < 0 ? 'forwards' : 'backwards'

      if (isFirefox) {
        this.scroll.delta *= this.settings.speedFirefox
      }

      if (this.options.snap) {
        this.checkGestures(this.scroll.delta)

        this.snapTimeOut = setTimeout(() => {
          this.snapToClosest()
        }, 500)
      }

      this.wheelTimeOut = setTimeout(() => {
        if (!this.pointer.move) this.stop()
      }, 800)
    } else {
      this.scroll.current = -document.body.scrollTop
    }
  }

  toPrevious() {
    if (!this.state.active) return false

    this.clearSnapTl()

    clearTimeout(this.wheelTimeOut)
    clearTimeout(this.prevTl)

    this.state.animating = true

    if (this.index.current > 0) this.index.current--

    Events.emit(`${this.sections[this.index.current].id}_PROGRESS`)
    // this.scrollTo(-this.sections[this.index.current].elBounds.offsetTop)

    if (this.options.horizontal) {
      this.scrollTo(-this.sections[this.index.current].elBounds.offsetLeft)
    } else {
      this.scrollTo(-this.sections[this.index.current].elBounds.offsetTop)
    }

    this.prevTl = setTimeout(() => {
      this.state.animating = false
    }, this.settings.scrollToDuration / 2)
  }

  toNext() {
    if (!this.state.active) return false

    this.clearSnapTl()

    clearTimeout(this.wheelTimeOut)
    clearTimeout(this.nextTl)

    this.state.animating = true

    if (this.index.total - 1 > this.index.current) this.index.current++

    Events.emit(`${this.sections[this.index.current].id}_PROGRESS`)
    if (this.options.horizontal) {
      this.scrollTo(-this.sections[this.index.current].elBounds.offsetLeft)
    } else {
      this.scrollTo(-this.sections[this.index.current].elBounds.offsetTop)
    }

    this.nextTl = setTimeout(() => {
      this.state.animating = false
    }, this.settings.scrollToDuration / 2)
  }

  onKeyDown(e) {
    if (Device.DEBUG) return
    if (!this.state.active) return false

    switch (e.code) {
      case 'ArrowUp':
        if (this.options.snap) {
          this.toPrevious()
        } else {
          this.scroll.current += 150
        }
        break
      case 'ArrowDown':
        if (this.options.snap) {
          this.toNext()
        } else {
          this.scroll.current -= 150
        }
        break
      case 'PageUp':
        this.scroll.current += this.layout.h
        break
      case 'PageDown':
        this.scroll.current -= this.layout.h
        break
      case 'Space':
        if (!(document.activeElement instanceof HTMLInputElement) && !(document.activeElement instanceof HTMLTextAreaElement)) {
          if (e.shiftKey) {
            this.scroll.current += this.layout.h
          } else {
            this.scroll.current -= this.layout.h
          }
        }
        break
    }
  }

  getSection(el) {
    let section

    this.sections.forEach((s) => {
      if (s.el === el) section = s
    })

    return section
  }

  getSections() {
    this.sections = []

    this.index.total = this.els.length

    for (let i = 0; i < this.els.length; i++) {
      const section = this.els[i]
      const parent = section.closest(this.selectorGroup)

      this.sections.push({
        active: false,
        el: section,
        id: Id('SCROLL_SECTION'),
        images: App.images && App.images.planes ? App.images.planes.getPlanes(section) : [],
        elBounds: {
          offsetTop: parent ? parent.offsetTop : section.offsetTop,
          offsetHeight: parent ? parent.offsetHeight : section.offsetHeight,
          offsetWidth: parent ? parent.offsetWidth : section.offsetWidth,
          offsetLeft: parent ? parent.offsetLeft : section.offsetLeft,
          w: this.layout.w,
          h: this.layout.h,
          current: 0,
        },
        scrollOffset: 0,
        inView: true,
        partsTop: [],
        partsBottom: [],
        parts: [],
        parent: parent,
      })
    }

    for (let i = 0; i < this.sections.length; i++) {
      const section = this.sections[i]

      const partsTop = section.el.querySelectorAll('[data-parallax-top]')
      const parts = section.el.querySelectorAll('[data-parallax]')
      const partsBottom = section.el.querySelectorAll('[data-parallax-bottom]')

      if (partsTop.length) {
        for (let j = 0; j < partsTop.length; j++) {
          const part = partsTop[j]
          section.partsTop.push({
            el: part,
            parallax: Device.GL ? part.dataset.parallaxTop || 1 : 1,
          })
        }
      }

      if (partsBottom.length) {
        for (let j = 0; j < partsBottom.length; j++) {
          const part = partsBottom[j]
          section.partsBottom.push({
            el: part,
            parallax: Device.GL ? part.dataset.parallaxBottom || 1 : 1,
          })
        }
      }

      if (parts.length) {
        for (let j = 0; j < parts.length; j++) {
          const part = parts[j]
          const { top, bottom } = section.el.getBoundingClientRect()

          part.transform = { x: 0, y: 0 }

          const partObj = {
            top,
            bottom,
            el: part,
            debug: part.dataset.scrollDebug !== undefined,
            middle: (bottom - top) / 2 + top,
            parallax: Device.GL ? part.dataset.parallax / 10 || 0 : 0,
          }

          if (partObj.debug) console.dir(partObj)

          section.parts.push(partObj)
        }
      }
    }
  }

  scrollSize() {
    if (!this.options.horizontal) {
      if (this.container) this.scroll.max = this.container.getBoundingClientRect().height - this.layout.h
    } else {
      let totalWidth = 0

      for (let i = 0; i < this.sections.length; i++) {
        const { offsetWidth } = this.sections[i].elBounds
        totalWidth += offsetWidth
      }

      if (this.container) this.scroll.max = this.container.getBoundingClientRect().left + totalWidth - this.layout.w
    }
  }

  reset() {
    const els = [...this.el.querySelectorAll(this.selectorChild), ...this.el.querySelectorAll('[data-parallax-top]'), ...this.el.querySelectorAll('[data-parallax]')]

    for (let index = 0; index < els.length; index++) {
      els[index].style.transform = ''
    }
  }

  calcSizes() {
    this.getSections()
    this.scrollSize()
  }

  onResize() {
    this.layout.h = window.innerHeight
    this.layout.w = window.innerWidth

    this.reset()

    this.resizing = true

    this.calcSizes()
    this.checkInView(this.sections)

    this.resizing = false

    Events.emit(EventType.SCROLL_RESIZE)
  }

  makeScrollBar() {
    this.scrollbar = document.createElement('span')
    this.scrollbarThumb = document.createElement('span')

    this.scrollbar.classList.add('scrollbar')
    this.scrollbarThumb.classList.add('scrollbar-thumb')

    this.scrollbar.append(this.scrollbarThumb)

    document.body.append(this.scrollbar)

    this.hasScrollbar = false
  }

  addScrollbarEvents() {
    window.addEventListener('mouseup', this.releaseScrollBar)
    window.addEventListener('mousemove', this.moveScrollBar)
  }

  deactivatePointerEvents() {
    if (!this.clickable) return
    this.el.classList.add('is-dragging')
    this.clickable = false
  }

  activatePointerEvents() {
    if (this.clickable) return
    this.el.classList.remove('is-dragging')
    this.clickable = true
  }
}
